/**
 * ProfileEditPage — 프로필 수정 (Figma 1511:46792).
 *
 * 닉네임 + 소개(Bio) → PUT /api/v1/user/members/me.
 * 프로필 사진 → POST /api/v1/user/members/me/profile-image (파일 선택 시 즉시 업로드).
 * 마운트 시 fetchMe 로 최신 값 로드 — 콜드스타트로 authStore.user 가 비어 있어도
 * 페이지는 렌더하고 Me 조회로 채운다 (user null 시 blank 회귀 방지).
 */
import { useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "../../store/authStore";
import { Button } from "@astryxdesign/core/Button";
import { Heading } from "@astryxdesign/core/Heading";
import { Text } from "@astryxdesign/core/Text";
import { TextInput } from "@astryxdesign/core/TextInput";
import { VStack } from "@astryxdesign/core/Layout";
import Icon from "../../components/primitives/Icon";
import { useToast } from "../../components/feedback/Toast";
import { fetchMe, updateMyProfile, uploadMyProfileImage } from "../../api/member";
import styles from "./ProfileEditPage.module.css";

// 닉네임은 provider 이름을 그대로 받으므로 8자로는 잘린다(2026-08-04 가입 화면 제거).
const NICKNAME_MAX = 20;
const BIO_MAX = 20;
/** 프로필 사진 최대 5MB (SC-033 · FN-080, design 2026-08-28) */
const PROFILE_IMAGE_MAX_BYTES = 5 * 1024 * 1024;
const ACCEPT_MIMES = "image/jpeg,image/png,image/webp,image/gif";

export default function ProfileEditPage() {
  const { t } = useTranslation(["me", "common"]);
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const [nickname, setNickname] = useState(user?.nickname ?? "");
  const [bio, setBio] = useState(user?.introduction ?? "");
  const [profileImageUrl, setProfileImageUrl] = useState<string | null>(
    user?.profileImageUrl ?? null,
  );
  const [submitting, setSubmitting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const toast = useToast();

  useEffect(() => {
    let cancelled = false;
    fetchMe()
      .then((me) => {
        if (cancelled || !me) return;
        setNickname(me.nickname ?? "");
        setBio(me.introduction ?? "");
        setProfileImageUrl(me.profileImageUrl ?? null);
        // 콜드스타트로 store user 가 비어 있어도 Me 로 통째 복구.
        setUser(me);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePickFile = () => {
    if (uploadingImage) return;
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // 같은 파일 재선택 가능하도록 input value 리셋
    if (e.target) e.target.value = "";
    if (!file) return;
    /* 올리기 전에 자른다 — 서버까지 갔다 오면 한참 뒤에 실패해서 왜 안 되는지 모른다.
       512×512 크롭·GIF 첫 프레임은 서버 몫이다 (FN-080). */
    if (file.size > PROFILE_IMAGE_MAX_BYTES) {
      toast.error(t("profile.photoTooLarge"));
      return;
    }
    setUploadingImage(true);
    try {
      const me = await uploadMyProfileImage(file);
      setProfileImageUrl(me.profileImageUrl ?? null);
      setUser(me);
      toast.success?.(t("profile.photoChanged"));
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : t("profile.photoFailed"),
      );
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const trimmedNickname = nickname.trim();
    if (!trimmedNickname) {
      setError(t("profile.nicknameRequired"));
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const trimmedBio = bio.trim();
      const updated = await updateMyProfile({
        nickname: trimmedNickname,
        introduction: trimmedBio.length > 0 ? trimmedBio : "",
      });
      setUser(updated);
      navigate("/me");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("profile.saveFailed"));
    } finally {
      setSubmitting(false);
    }
  };

  const avatarChar = (
    nickname[0] ??
    user?.nickname?.[0] ??
    user?.email?.[0] ??
    "?"
  ).toUpperCase();

  return (
    <form className={styles.page} onSubmit={handleSubmit}>
      <div className={styles.content}>
        {/* profilePhotoSection (Figma 893:16356 — 150x150 + edit overlay 893:16387) */}
        <section className={styles.photoSection}>
          <div className={styles.photoBox}>
            {profileImageUrl ? (
              <img
                className={styles.photoImage}
                src={profileImageUrl}
                alt={t("profile.photoAlt")}
              />
            ) : (
              <span className={styles.photoChar} aria-hidden>
                {avatarChar}
              </span>
            )}
            <button
              type="button"
              className={styles.photoEditOverlay}
              aria-label={t("profile.photoChange")}
              onClick={handlePickFile}
              disabled={uploadingImage}
            >
              <Icon name="Camera" size="lg" />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPT_MIMES}
              className={styles.hiddenFileInput}
              onChange={handleFileChange}
            />
          </div>
        </section>

        {/* 기본 정보 (Figma 893:16118).
            ⚠ 2026-08-25 — 자체 primitives/TextInput 에서 Astryx 로 갈아 끼웠다.
              라벨·필수 표시·글자 수 세기를 손수 그리고 있어서, Astryx 를 쓰는
              다른 화면들과 입력란 모양이 달랐다. */}
        <section className={styles.contentSection}>
          <VStack gap={4}>
            <Heading level={3}>{t("profile.sectionTitle")}</Heading>

            {/* 글자 수(0/8자)는 Astryx 가 description 자리에 직접 그린다 —
                전에는 입력란 밖에 span 을 따로 두어 오른쪽 아래에 띄웠다. */}
            <TextInput
              label={t("profile.nickname")}
              isRequired
              placeholder={t("profile.nicknamePlaceholder")}
              value={nickname}
              onChange={(v) => setNickname(v.slice(0, NICKNAME_MAX))}
              description={t("profile.charCount", { len: nickname.length, max: NICKNAME_MAX })}
            />

            <TextInput
              label={t("profile.bio")}
              placeholder={t("noIntroduction")}
              value={bio}
              onChange={(v) => setBio(v.slice(0, BIO_MAX))}
              description={t("profile.charCount", { len: bio.length, max: BIO_MAX })}
            />
          </VStack>
        </section>
      </div>

      <div className={styles.ctaSection}>
        <VStack gap={2}>
          {/* ⚠ Astryx Text 에는 오류 색이 없다(primary·secondary·accent…).
              토큰을 className 으로 얹는다 — DESIGNER §2 의 허용 경로다. */}
          {error && (
            <Text type="supporting" className={styles.error}>{error}</Text>
          )}
          <Button
            variant="primary"
            size="lg"
            type="submit"
            label={submitting ? t("profile.saving") : t("common:action.save")}
            isDisabled={submitting}
            width="100%"
          />
        </VStack>
      </div>
    </form>
  );
}
